import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ComingSoonModal, Skeleton } from "@templestay/ui";
import { useLangStore } from "@/store/lang.js";
import { PublicNav } from "@/components/PublicNav.js";
import { useTestResult } from "@/hooks/useTestResult.js";
import { uiStrings } from "@templestay/i18n";
import { resolveText } from "@/lib/i18nResolve.js";
import { usePageTitle } from "@/hooks/usePageTitle.js";
import type { Language } from "@templestay/i18n";

function useOgMeta(title: string, description: string, imageUrl: string) {
  useEffect(() => {
    const set = (prop: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[property="${prop}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setName = (name: string, content: string) => {
      let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    document.title = title;
    set("og:title", title); set("og:description", description); set("og:image", imageUrl); set("og:type", "website");
    setName("twitter:card", "summary_large_image"); setName("twitter:title", title); setName("twitter:description", description); setName("twitter:image", imageUrl);
  }, [title, description, imageUrl]);
}

export function TestResultPage() {
  const { lang } = useLangStore();
  const { slug, resultSlug } = useParams<{ slug: string; resultSlug: string }>();
  const [showKakaoModal, setShowKakaoModal] = useState(false);
  const s = uiStrings[lang];

  const { data: resultType, isLoading } = useTestResult(slug, resultSlug);

  function getI18n(i18nStr: string | undefined) {
    if (!i18nStr) return {} as Record<string, string>;
    const obj = JSON.parse(i18nStr) as Record<Language, Record<string, string>>;
    return resolveText(obj, lang, "ko");
  }

  const rt = resultType ? getI18n(resultType.i18n) : null;
  const ogImageUrl = slug && resultSlug ? `/api/og/test/${slug}/${resultSlug}?lang=${lang}` : "/og-default.svg";

  usePageTitle(rt?.title ? `${s.site.name} — ${rt.title}` : s.site.testTitle);
  useOgMeta(
    rt?.title ? `${s.site.name} — ${rt.title}` : s.site.name,
    rt?.desc ?? "봉은사 성격 유형 검사 결과",
    ogImageUrl
  );

  function handleShare() {
    const url = window.location.href;
    if (navigator.share) navigator.share({ title: rt?.title ?? "봉은사", url }).catch(() => {});
    else navigator.clipboard.writeText(url).catch(() => {});
  }

  return (
    <div className="flex min-h-screen flex-col pt-3">
      <PublicNav lang={lang} />

      {isLoading ? (
        <div className="px-3 pt-3 space-y-4">
          <div className="rounded-2xl glass-card px-5 py-6 space-y-4">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-48 w-full rounded-xl" />
            <Skeleton className="h-20 w-full rounded-xl" />
          </div>
        </div>
      ) : (
        <div className="px-3 pt-3 pb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center gap-5 rounded-2xl glass-card px-5 py-7 text-center"
          >
            <p className="text-xs font-semibold uppercase tracking-widest text-brand-500">Who am I</p>

            {rt ? (
              <>
                <div>
                  <p className="text-sm font-medium text-brand-400">{rt.subtitle}</p>
                  <h1 className="mt-1 text-3xl font-bold text-gray-900">{rt.title}</h1>
                </div>

                {resultType?.image_url ? (
                  <img
                    src={resultType.image_url}
                    alt={rt.title ?? ""}
                    className="w-full rounded-xl object-cover shadow-md"
                    style={{ aspectRatio: "4/3" }}
                  />
                ) : (
                  <div
                    className="relative flex w-full flex-col items-center justify-center gap-3 overflow-hidden rounded-2xl px-6 py-10"
                    style={{ background: "linear-gradient(135deg, rgba(240,244,255,0.8) 0%, rgba(232,240,254,0.8) 40%, rgba(253,244,255,0.8) 100%)" }}
                  >
                    <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-brand-100/40" />
                    <div className="absolute -bottom-6 -left-6 h-24 w-24 rounded-full bg-purple-100/40" />
                    <p className="relative text-5xl">🏛️</p>
                    {rt.subtitle && <p className="relative text-xs font-semibold text-brand-500">{rt.subtitle}</p>}
                    <p className="relative text-lg font-bold text-gray-800">{rt.title}</p>
                  </div>
                )}

                <p className="text-sm leading-relaxed text-gray-600">{rt.desc}</p>

                {rt.temple_note && (
                  <div className="w-full rounded-2xl bg-amber-50/80 px-4 py-3 text-left ring-1 ring-amber-200/50">
                    <p className="text-xs leading-relaxed text-amber-700">{rt.temple_note}</p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex h-48 w-full items-center justify-center rounded-2xl bg-brand-50/50">
                <p className="text-brand-300">{s.test.resultNotFound}</p>
              </div>
            )}

            <div className="w-full space-y-2.5">
              <button
                onClick={handleShare}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-white/70 py-3.5 text-sm font-semibold text-brand-600 ring-1 ring-brand-200/60 transition-colors hover:bg-brand-50/80 backdrop-blur-sm"
              >
                {s.test.shareResult}
              </button>
              <button
                onClick={() => setShowKakaoModal(true)}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#FEE500] py-3.5 text-sm font-semibold text-[#3A1D1D] transition-colors hover:brightness-95"
              >
                {s.test.shareKakao}
              </button>
            </div>

            <a href="/" className="mt-1 text-sm text-gray-400 underline underline-offset-2">
              {s.test.backHome}
            </a>
          </motion.div>
        </div>
      )}

      <ComingSoonModal
        open={showKakaoModal}
        onClose={() => setShowKakaoModal(false)}
        lang={lang}
        description={lang === "ko" ? "카카오톡 공유는 본 개발에서 SDK 연동 후 제공됩니다. URL 공유를 이용해 주세요." : s.common.comingSoonDesc}
      />
    </div>
  );
}
